/**
 * relationship.ts — CLIENT MIRROR of server/src/lib/relationship_ops.ts's
 * pure math (the town_layout mirror law — change one, change both):
 * decayed event weights, the net opinion, the disclosure-ladder tiers, and
 * the age buckets dialogue uses to cite events ("not a week past…").
 * The server value is always authoritative; this renders it.
 */

export const REL_EVENT_CAP = 12;
export const NEMESIS_NET = -60;
export const HOSTILE_NET = -35;
export const COLD_NET = -15;
export const FRIEND_NET = 30;
export const CONFIDANT_NET = 60;

export interface RelEventLike { weight: number; halfLifeS: number; at: { microsSinceUnixEpoch: bigint } }

/** One event's current (decayed) weight — halfLife 0 never fades. */
export function decayedWeight(e: RelEventLike, nowMs: number): number {
  if (e.halfLifeS === 0) return e.weight;
  const ageS = Math.max(0, (nowMs - Number(e.at.microsSinceUnixEpoch / 1000n)) / 1000);
  return e.weight * Math.pow(0.5, ageS / e.halfLifeS);
}

/** The NET opinion: Σ decayed weights, clamped ±100. */
export function relNetOf(events: RelEventLike[], nowMs: number): number {
  let n = 0;
  for (const e of events) n += decayedWeight(e, nowMs);
  return Math.max(-100, Math.min(100, Math.round(n)));
}

/**
 * DISPOSITION (2026-09-15, the versona review's item 3): one net opinion cannot
 * tell fear from dislike or warmth from trust. Each relation event feeds FOUR
 * axes by its KIND — multipliers on its |decayed weight|:
 *   warmth      greeting, company (gifts, talk)
 *   trust       what they will tell you / do with you (favors, amends; harm and ill report break it)
 *   fear        distance and a wary eye (harm — permanent, never erased by gifts; amends ease it)
 *   obligation  what they owe you (favors you did)
 * MIRROR LAW: this function is byte-identical in server/src/lib/relationship_ops.ts
 * and client/src/lib/relationship.ts (the harness compares the two bodies).
 */
export interface Disposition { warmth: number; trust: number; fear: number; obligation: number }
export function dispositionAxes(kind: string, w: number): Disposition {
  const a = Math.abs(w);
  switch (kind) {
    case 'harmed': return { warmth: -a, trust: -a, fear: a, obligation: 0 };
    case 'heard_ill': return { warmth: -a * 0.5, trust: -a, fear: a * 0.5, obligation: 0 };
    case 'gift_bad': return { warmth: -a, trust: 0, fear: 0, obligation: 0 };
    case 'gift': return { warmth: a, trust: a * 0.25, fear: 0, obligation: 0 };
    case 'favor_done': case 'defended': return { warmth: a * 0.5, trust: a, fear: 0, obligation: a };
    case 'apology': case 'amends': return { warmth: a * 0.5, trust: a, fear: -a, obligation: 0 };
    case 'talk': return { warmth: a, trust: a * 0.25, fear: 0, obligation: 0 };
    case 'rivalry': return { warmth: 0, trust: 0, fear: 0, obligation: 0 };
    default: return w >= 0 ? { warmth: a, trust: a * 0.5, fear: 0, obligation: 0 } : { warmth: -a, trust: -a, fear: a * 0.5, obligation: 0 };
  }
}
/** A versona who fears you this much keeps its distance: the merchant will not serve you, the eyes never leave you. */
export const FEAR_REFUSE = 25;
const clampAxis = (v: number, lo: number): number => Math.max(lo, Math.min(100, v));
export function dispositionOf(events: RelEventLike[], nowMs: number): Disposition {
  const d: Disposition = { warmth: 0, trust: 0, fear: 0, obligation: 0 };
  for (const e of events) {
    const x = dispositionAxes((e as { kind?: string }).kind ?? '', decayedWeight(e, nowMs));
    d.warmth += x.warmth; d.trust += x.trust; d.fear += x.fear; d.obligation += x.obligation;
  }
  return { warmth: clampAxis(d.warmth, -100), trust: clampAxis(d.trust, -100), fear: clampAxis(d.fear, 0), obligation: clampAxis(d.obligation, 0) };
}

export type RelTier = 'nemesis' | 'hostile' | 'cold' | 'stranger' | 'familiar' | 'friend' | 'confidant';

/** The disclosure ladder (never surfaced as a number — behavior only). */
export function tierOf(net: number, seenCount: number, nemesis: boolean): RelTier {
  if (nemesis) return 'nemesis';
  if (net <= HOSTILE_NET) return 'hostile';
  if (net <= COLD_NET) return 'cold';
  if (net >= CONFIDANT_NET && seenCount >= 12) return 'confidant';
  if (net >= FRIEND_NET && seenCount >= 3) return 'friend';
  if (net >= 8 || seenCount >= 5) return 'familiar';
  return 'stranger';
}

/** How dialogue dates a memory ("%label%, %ago%"). */
export function agoBucket(atMicros: bigint, nowMs: number): string {
  const days = (nowMs - Number(atMicros / 1000n)) / 86_400_000;
  if (days < 1) return 'today';
  if (days < 7) return 'not a week past';
  if (days < 30) return 'a while back';
  return 'long ago';
}


/** Mirror of server memory_ops.ASPIRATION_LABEL (first-person dream lines). */
export const ASPIRATION_LABEL: Record<string, string> = {
  wealth: 'to fill a strongbox of my own — three hundred coins, honestly earned',
  mastery: 'to be named a master of my craft before this town',
  pilgrim: 'to walk roads far from home and come back with stories',
  devotion: 'to keep every rite of my faith, every season',
  founder: 'to help raise something new in this town with my own hands',
};
